// Ported verbatim from MailBaser; types erased for Vue 2 / Node 14. See provenance.json.
// Shared form model: the builder edits this shape, the public page renders
// it, and the embed script mirrors the button config. Field widths implement
// the row/column system — a 6-track grid where full=6, half=3, third=2 —
// so "first name, last name, submit on one row" is three third-width items.
export const FORM_FOOTER_DEFAULT = "We respect your privacy.";
export const FORM_ACCENT_FALLBACK = "#4f46e5";
export const FORM_CUSTOM_CSS_MAX_LENGTH = 10000;
export const formFontOptions = [
    { value: "", label: "App default", stack: "" },
    {
        value: "system",
        label: "System",
        stack: "ui-sans-serif, system-ui, -apple-system, sans-serif",
    },
    {
        value: "inter",
        label: "Inter",
        stack: "'Inter', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Inter",
    },
    {
        value: "nunito-sans",
        label: "Nunito Sans",
        stack: "'Nunito Sans', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Nunito Sans",
    },
    {
        value: "open-sans",
        label: "Open Sans",
        stack: "'Open Sans', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Open Sans",
    },
    {
        value: "roboto",
        label: "Roboto",
        stack: "'Roboto', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Roboto",
    },
    {
        value: "montserrat",
        label: "Montserrat",
        stack: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Montserrat",
    },
    {
        value: "poppins",
        label: "Poppins",
        stack: "'Poppins', ui-sans-serif, system-ui, sans-serif",
        googleFamily: "Poppins",
    },
    {
        value: "lora",
        label: "Lora",
        stack: "'Lora', Georgia, serif",
        googleFamily: "Lora",
    },
    {
        value: "merriweather",
        label: "Merriweather",
        stack: "'Merriweather', Georgia, serif",
        googleFamily: "Merriweather",
    },
    {
        value: "playfair-display",
        label: "Playfair Display",
        stack: "'Playfair Display', Georgia, serif",
        googleFamily: "Playfair Display",
    },
    { value: "georgia", label: "Georgia", stack: "Georgia, 'Times New Roman', serif" },
];
export function formFontByValue(value) {
    var _a;
    return ((_a = formFontOptions.find((option) => option.value === value)) !== null && _a !== void 0 ? _a : formFontOptions[0]);
}
export function formFontStack(value) {
    return formFontByValue(value).stack;
}
export function formFontStylesheetUrl(value) {
    const option = formFontByValue(value);
    if (!option.googleFamily) {
        return "";
    }
    const family = option.googleFamily.replace(/ /g, "+");
    return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
}
/**
 * Custom CSS ships inside a <style> tag on the public form page, so the only
 * hard requirement is that it cannot close that tag and break into markup.
 */
export function sanitizeFormCustomCss(value) {
    if (typeof value !== "string") {
        return "";
    }
    return value
        .replace(/<\s*\/\s*style/gi, "")
        .slice(0, FORM_CUSTOM_CSS_MAX_LENGTH)
        .trim();
}
export const formWidthClass = {
    full: "col-span-6",
    twothirds: "col-span-6 sm:col-span-4",
    half: "col-span-6 sm:col-span-3",
    third: "col-span-6 sm:col-span-2",
};
export const formWidthOptions = [
    { value: "full", label: "Full" },
    { value: "twothirds", label: "2/3" },
    { value: "half", label: "1/2" },
    { value: "third", label: "1/3" },
];
export const formButtonIcons = [
    { value: "none", label: "No icon" },
    { value: "arrow", label: "Arrow" },
    { value: "send", label: "Send" },
    { value: "mail", label: "Mail" },
    { value: "calendar", label: "Calendar" },
    { value: "download", label: "Download" },
    { value: "sparkles", label: "Sparkles" },
    { value: "gift", label: "Gift" },
];
export const formButtonStyles = [
    { value: "solid", label: "Solid" },
    { value: "soft", label: "Soft" },
    { value: "outline", label: "Outline" },
    { value: "pill", label: "Pill" },
    { value: "dark", label: "Dark" },
];
export function defaultFormButton() {
    return {
        style: "solid",
        label: "Open the form",
        sublabel: "",
        icon: "arrow",
        iconPosition: "right",
    };
}
export function defaultFormDesign() {
    return {
        heading: "Join our list",
        description: "Get useful updates from our team.",
        accentColor: FORM_ACCENT_FALLBACK,
        backgroundColor: "",
        labelStyle: "stacked",
        radius: "xl",
        fontFamily: "",
        customCss: "",
        footerText: FORM_FOOTER_DEFAULT,
        submitLabel: "Subscribe",
        submitSublabel: "",
        submitWidth: "full",
        submitHeight: "md",
        submitColor: "",
        submitTextColor: "#ffffff",
        submitIcon: "none",
        submitIconPosition: "right",
        button: defaultFormButton(),
    };
}
export function parseFormFields(value) {
    const fallback = [
        {
            key: "email",
            label: "Email",
            placeholder: "you@example.com",
            type: "email",
            required: true,
            options: [],
            width: "full",
        },
    ];
    if (!Array.isArray(value)) {
        return fallback;
    }
    const fields = value
        .map((field) => objectValue(field))
        .filter((field) => stringValue(field.key))
        .map((field) => ({
        key: stringValue(field.key),
        label: stringValue(field.label) || stringValue(field.key),
        placeholder: stringValue(field.placeholder),
        type: stringValue(field.type) || "text",
        required: field.required === true,
        options: Array.isArray(field.options)
            ? field.options.filter((option) => typeof option === "string")
            : [],
        width: parseWidth(field.width),
    }));
    return fields.some((field) => field.key === "email") ? fields : fallback;
}
export function parseFormDesign(value) {
    var _a, _b;
    const design = objectValue(value);
    const button = objectValue(design.button);
    const defaults = defaultFormDesign();
    return {
        heading: stringValue(design.heading),
        description: stringValue(design.description),
        accentColor: safeCssColor(design.accentColor) || defaults.accentColor,
        backgroundColor: safeCssColor(design.backgroundColor),
        labelStyle: design.labelStyle === "hidden" ? "hidden" : "stacked",
        radius: design.radius === "md" ? "md" : "xl",
        fontFamily: formFontOptions.some((option) => option.value === design.fontFamily)
            ? design.fontFamily
            : "",
        customCss: sanitizeFormCustomCss(design.customCss),
        // Absent key = legacy form, gets the default; explicit "" = user cleared.
        footerText: typeof design.footerText === "string"
            ? design.footerText
            : FORM_FOOTER_DEFAULT,
        submitLabel: stringValue(design.submitLabel) || "Submit",
        submitSublabel: stringValue(design.submitSublabel),
        submitWidth: design.submitWidth === "auto"
            ? "auto"
            : parseWidth((_a = design.submitWidth) !== null && _a !== void 0 ? _a : "full"),
        submitHeight: design.submitHeight === "sm" || design.submitHeight === "lg"
            ? design.submitHeight
            : "md",
        submitColor: safeCssColor(design.submitColor),
        submitTextColor: safeCssColor(design.submitTextColor) || "#ffffff",
        submitIcon: design.submitIcon === "none" ? "none" : parseButtonIcon((_b = design.submitIcon) !== null && _b !== void 0 ? _b : "none"),
        submitIconPosition: design.submitIconPosition === "left" ? "left" : "right",
        button: {
            style: parseButtonStyle(button.style),
            label: stringValue(button.label) || defaults.button.label,
            sublabel: stringValue(button.sublabel),
            icon: parseButtonIcon(button.icon),
            iconPosition: button.iconPosition === "left" || button.iconPosition === "both"
                ? button.iconPosition
                : "right",
        },
    };
}
export function parseFormFieldWidth(value) {
    return value === "half" || value === "third" || value === "twothirds"
        ? value
        : "full";
}
const parseWidth = parseFormFieldWidth;
function parseButtonStyle(value) {
    return value === "soft" ||
        value === "outline" ||
        value === "pill" ||
        value === "dark"
        ? value
        : "solid";
}
function parseButtonIcon(value) {
    return value === "none" ||
        value === "send" ||
        value === "mail" ||
        value === "calendar" ||
        value === "download" ||
        value === "sparkles" ||
        value === "gift"
        ? value
        : "arrow";
}
export function safeCssColor(value) {
    if (typeof value !== "string") {
        return "";
    }
    const trimmed = value.trim();
    return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(trimmed) ? trimmed : "";
}
function objectValue(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function stringValue(value) {
    return typeof value === "string" ? value : "";
}
